import React from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { useDashT } from "../lib/dashboardStrings";

/**
 * Top-level Error Boundary for the dashboard shell.
 *
 * A single component-level crash (bad response shape, missing prop, thrown
 * inside a render) will no longer take down the whole app. Instead the user
 * sees a compact fallback card with "Reload" / "Go to dashboard" buttons and
 * the shell remains navigable via the sidebar (which is rendered OUTSIDE this
 * boundary from the caller's perspective).
 *
 * Copy routed through `dt()` for EN + SV, no em-dashes.
 * The boundary auto-resets when the page path changes so an unrelated route
 * click after a crash "unsticks" the user.
 */
class DashboardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[DashboardErrorBoundary]", error, info?.componentStack);
    // Best-effort: notify any monitoring layer without breaking on failure.
    try {
      if (window?.posthog?.capture) {
        window.posthog.capture("dashboard_error", {
          message: String(error?.message || error),
          stack: String(error?.stack || "").slice(0, 4000),
          component: String(info?.componentStack || "").slice(0, 2000),
        });
      }
    } catch (_) { /* ignore */ }
  }
  componentDidUpdate(prevProps) {
    // Reset the boundary when the router path changes so an old crash doesn't
    // haunt the user forever.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    return <ErrorFallback error={this.state.error}
                            onReload={() => window.location.reload()}
                            onHome={() => { window.location.href = "/"; }} />;
  }
}

const ErrorFallback = ({ error, onReload, onHome }) => {
  const dt = useDashT();
  const message = String(error?.message || "").slice(0, 200);
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="dashboard-error-boundary-fallback">
      <div className="w-full max-w-[520px] rounded-2xl border border-[#2A2A2A] bg-[#181818] p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-[#FF5C00]/12 flex items-center justify-center mx-auto">
          <AlertTriangle size={26} className="text-[#FF5C00]" />
        </div>
        <h2 className="text-[22px] font-bold text-white mt-5">{dt("Something went wrong")}</h2>
        <p className="text-neutral-400 mt-2 text-[14px]">
          {dt("A part of this page failed to load. Try reloading, or go back to your dashboard.")}
        </p>
        {message && (
          <details className="mt-4 text-left">
            <summary className="text-[12px] text-neutral-500 cursor-pointer select-none">
              {dt("Technical details")}
            </summary>
            <pre className="mt-2 p-3 rounded-lg bg-[#0F0F0F] border border-[#2A2A2A] text-[11px] text-neutral-400 whitespace-pre-wrap break-words max-h-[160px] overflow-auto">
{message}
            </pre>
          </details>
        )}
        <div className="flex gap-3 justify-center mt-6 flex-wrap">
          <button data-testid="error-boundary-reload" onClick={onReload}
            className="inline-flex items-center gap-2 bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold px-5 py-2.5 rounded-lg transition-colors">
            <RefreshCw size={16} /> {dt("Reload")}
          </button>
          <button data-testid="error-boundary-home" onClick={onHome}
            className="inline-flex items-center gap-2 bg-[#181818] border border-[#2A2A2A] text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-[#232326] transition-colors">
            <Home size={16} /> {dt("Go to dashboard")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardErrorBoundary;
